import { Container } from '@tencent/alloy-renderer';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { RendererShapeComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-shape-component';
import { RendererImageComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-image-component';
import { RendererConnectorComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-connector-component';
import { RendererTableComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-table-component';
import { RendererGroupComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-group-component';
import { RendererSmartArtComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-smart-art-component';
import { ComponentWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-wrapper';
import { ComponentAnimWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-anim-wrapper';
import { generateStates } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-append-states';
import ObserverFactory from '@src/slide/application/service/modelObserver/observerFactory';
import { reaction } from 'mobx';
import { RendererChartComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-chart-component';
import { componentMakeCache } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/layered-render/component-cache-help';
import cloneDeep from 'lodash/cloneDeep';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import type { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import type { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import type { IComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory.interface';
import type { IReactionDisposer } from 'mobx';
import type BaseObserver from '@src/slide/application/service/modelObserver/baseObserver';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { ISmartArtModel } from '@src/slide/domain/ui-model/element-modeles/Interface/smart-art-model.interface';
import { isShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';

export class ComponentFactory implements IComponentFactory {
    // 生成图形的入参
    public readonly factoryOptions: InterfaceFactoryOptions;
    // 所有类型图形的动画包裹层，只有动画元素才加这一层
    public animWrapper: ComponentAnimWrapper;
    // 所有类型图形的包裹层，提供位移、旋转、翻转、通用事件等能力
    public wrapper: ComponentWrapper;
    // 实际各类图形的渲染，提供图形显示以及独有事件等
    public inner: any;
    // 数据监听，数据源变化触发图形刷新
    private observer: BaseObserver;
    // 权限变更监听
    private editStateReaction: IReactionDisposer;
    // 当前图形是否为选中态（用作更新对比用）
    private selected: boolean;
    private slideIndexReaction: IReactionDisposer;

    public constructor(factoryOptions: InterfaceFactoryOptions) {
        this.factoryOptions = factoryOptions;
        const { model } = factoryOptions;

        // 图形类型无效则直接截断
        if (!this.checkType(model.type)) {
            this.wrapper = new Container() as ComponentWrapper;
            return;
        }

        this.selected = model.selected;

        // 补充公共状态、渲染图形
        this.updateComponent();

        // 注册各监听（数据监听、事件监听）
        this.addAllListener();
    }

    public updateComponent = (): void => {
        try {
            this.appendOptionsStates();
            this.renderGraphic();
        } catch (e) {
            // weblog 吞了stack，开发环境打个error方便排查
            if (__IS_DEV__) {
                console.error(e);
            }

            this.wrapper = this.wrapper || new Container();
            this.inner = this.inner || new Container();
            window.log.error(`[canvas-component-factory]:${e}`);
            tdw({
                opername: TDWOperationType.PPT_QUALITY,
                module: TDWModuleType.CANVAS_RENDERER,
                action: TDWActionType.COMPONENT_FACTORY_RENDER,
                ver6: JSON.stringify({ errorMsg: e.message, style: this.factoryOptions.model.props.style }),
            });
        }
    };

    /**
     * 获取元素最外层wrapper
     * @returns ComponentAnimWrapper | ComponentWrapper
     */
    public getWrapper = (): ComponentAnimWrapper | ComponentWrapper  => this.animWrapper || this.wrapper;

    private addAllListener = () => {
        // 数据监听，驱动render层刷新
        this.addModelObserver();
        // 绑定数据刷新监听器、事件
        this.factoryOptions.logicService.setLogic(this.factoryOptions, this);
    };

    private removeAllListener = () => {
        this.removeModelObserver();
        this.factoryOptions.logicService.removeLogic(this.factoryOptions);
    };

    // 移除元素model监听
    private removeModelObserver = () => {
        if (this.observer) {
            this.observer.removeChangesetObserver(this.changeSetCallback);
            if (!this.factoryOptions.fromScale) {
                this.observer.removeLocalChangeObserver(this.localChangeCallback);
            }
        }
        this.editStateReaction?.();
        this.slideIndexReaction?.();
    };

    // 监听元素model变化，重新渲染元素
    private addModelObserver = () => {
        this.observer = ObserverFactory.createObserver(this.factoryOptions.model);
        if (this.observer) {
            this.observer.addChangesetObserver(this.changeSetCallback);
            if (!this.factoryOptions.fromScale) {
                this.observer.addLocalChangeObserver(this.localChangeCallback, true);
            }
        }
        // 权限变更监听
        this.editStateReaction = reaction(() => ({
            slideEditState: this.factoryOptions.model.currentEditState,
        }), this.changeSetCallback);

        this.slideIndexReaction = reaction(() => ({
            slideIndex: this.factoryOptions.slideModel?.slideIndex,
            editType: this.getMasterLayoutEditService()?.editType,
            firstSlideNum: this.factoryOptions.uiDataService?.presentation.firstSlideNum,
        }), () => {
            const { model } = this.factoryOptions;
            // 页码顺序变化或者退出母版编辑时，母版或者版式上带有页码属性的元素需要刷新
            if (isShapeModel(model) && model?.hasSlideNumber?.() && this.factoryOptions.slideModel?.isSlide) {
                model.setUpdateState({ editorShouldUpdate: true });
                this.changeSetCallback();
            }
        });
    };

    private changeSetCallback = async () => {
        const { model } = this.factoryOptions;
        this.factoryOptions.modelParams = cloneDeep({
            updateState: model.getUpdateState(),
        });
        this.factoryOptions.isEditable = !!this.factoryOptions.model.currentEditState;
        if (this.factoryOptions.fromScale) {
            // changeSet触发的更新，左侧缩略图较右侧编辑区延迟两个事件轮询，躲开编辑区的构建和渲染
            await new Promise(resolve => setTimeout(resolve));
            await new Promise(resolve => setTimeout(resolve));
        }
        this.updateComponent();

        if (this.factoryOptions.slideModel?.getBackgroundFill().getFillInfo()?.shadeToTitle
            && isShapeModel(this.factoryOptions.model)
            && this.factoryOptions.model.isTitleOrCtrTitlePl) {
            this.factoryOptions.refreshBackground?.();
        }

        // 如果当前图形处于选中态 则将符合缓存条件的图形进行缓存（大表格/组合）
        if (this.selected) {
            await new Promise(resolve => setTimeout(resolve));
            componentMakeCache(this.wrapper);
        }
    };

    private localChangeCallback = (changeData, modelParams) => {
        this.factoryOptions.modelParams = cloneDeep(modelParams);
        this.updateComponent();

        // 如果当前图形处于选中态 则将符合缓存条件的图形进行缓存（大表格/组合）
        if (changeData.selected !== this.selected) {
            this.selected = changeData.selected;
            if (this.selected) {
                componentMakeCache(this.wrapper);
            }
        }
    };

    // factoryOptions补充公共状态
    private appendOptionsStates = () => {
        Object.assign(
            this.factoryOptions,
            generateStates(this.factoryOptions as InterfaceFactoryOptions<IElementBaseModel>),
        );
    };

    // 图形渲染，首次插入，后续刷新
    private renderGraphic = () => {
        if (!this.inner) {
            // 首次插入
            this.wrapper = this.createWrapper();
            this.inner = this.createInner();
            this.wrapper.addChild(this.inner);
            this.createAnimWrapper();
        } else {
            // 后续刷新
            this.wrapper.updateComponent();
            // 处于性能考虑，拖拽时无需更新内部图形，因为位移只作用于wrapper（连接线除外，在拖拽时会变形）
            const { model: { type } } = this.factoryOptions;
            if (!this.factoryOptions.editingStateManager?.isDragging || type === UIModelType.CONNECTOR) {
                this.inner.updateComponent?.();
            }
        }
    };

    // 生成图形wrapper
    private createWrapper = (): ComponentWrapper => new ComponentWrapper(
        this.factoryOptions as InterfaceFactoryOptions<IElementBaseModel>,
        this.removeAllListener,
        this.updateComponent,
    );

    // 按类别生成实际图形
    private createInner = () => {
        const { model } = this.factoryOptions;
        switch (model.type) {
            case UIModelType.SHAPE:
                return new RendererShapeComponent(this.factoryOptions as InterfaceFactoryOptions<IShapeModel>);
            case UIModelType.IMAGE:
                return new RendererImageComponent(this.factoryOptions as InterfaceFactoryOptions<IPictureModel>);
            case UIModelType.CONNECTOR:
                return new RendererConnectorComponent(this.factoryOptions as InterfaceFactoryOptions<IConnectorModel>);
            case UIModelType.TABLE:
                return new RendererTableComponent(this.factoryOptions as InterfaceFactoryOptions<ITableModel>);
            case UIModelType.GROUP:
                return new RendererGroupComponent(this.factoryOptions as InterfaceFactoryOptions<IGroupModel>);
            case UIModelType.CHART:
                return new RendererChartComponent(this.factoryOptions as InterfaceFactoryOptions<IChartModel>);
            case UIModelType.SMART_ART:
                return new RendererSmartArtComponent(this.factoryOptions as InterfaceFactoryOptions<ISmartArtModel>);
            default:
                return null;
        }
    };

    // 创建animWrapper
    private createAnimWrapper = (): void | undefined => {
        // 元素已经有animWrapper，则也不必重复创建animWrapper
        if (this.animWrapper) return;
        // 元素无论是否有动画，都创建animWrapper，以避免动画元素进行多次撤销重做后，元素位置偏移问题
        this.animWrapper = new ComponentAnimWrapper(
            this.factoryOptions as InterfaceFactoryOptions<IElementBaseModel>,
            this.removeAllListener,
            this.updateComponent,
        );
        this.animWrapper.addChild(this.wrapper);
    };

    private checkType(type): boolean {
        return type === UIModelType.CONNECTOR
            || type === UIModelType.SHAPE
            || type === UIModelType.IMAGE
            || type === UIModelType.GROUP
            || type === UIModelType.TABLE
            || type === UIModelType.CHART
            || type === UIModelType.SMART_ART;
    }

    private getMasterLayoutEditService(): IMasterLayoutEditService | undefined {
        return this.factoryOptions.masterLayoutEditService;
    }
}
